import { SelectedValueDisplay } from '@ths/shared';
import { FieldValidator } from 'final-form';
import { useState } from 'react';
import { useField } from 'react-final-form';

import CreateGuestModal, {
  CreatedGuestValues,
  CreateGuestFormValues,
} from 'components/CreateGuestModal';
import ClientTeamMemberAutoComplete from 'components/Roster/ClientTeamMemberAutoComplete';
import { ReservationRequiredField } from 'generated/graphql';
import { ClientTeamMemberOption } from 'hooks/useClientClientTeamMemberSelector';
import { useClientRequiredFieldsAndPod } from 'hooks/useClientRequiredFieldsAndPod';
import { getGuestLevelFields } from 'utilities/guestFieldMapping';
import { parseNameFromQuery, sanitizeUserInput } from 'utilities/nameParser';
import { looksLikeDODID } from 'utilities/validators';
const selectionText = (value: ClientTeamMemberSelectOption) => {
  if ('firstName' in value && 'lastName' in value) {
    // Handle ClientTeamMemberOption or CreatedGuest
    const identifier =
      'semiUniqueIdentifier' in value
        ? value.semiUniqueIdentifier
        : value.edipi;

    const suffix =
      'id' in value ? (identifier ? ` (${identifier})` : '') : ' (New Guest)';

    return `${value.firstName} ${value.lastName}${suffix}`;
  }
  return '';
};

export type ClientTeamMemberSelectOption = ClientTeamMemberOption;

export default function ClientTeamMemberSelect({
  name,
  validate,
  testId,
  onAdd,
  disabled = false,
  allowCreateGuest = false,
}: {
  name: string;
  validate?: FieldValidator<ClientTeamMemberSelectOption | ''>;
  onAdd?: () => void;
  testId?: string;
  disabled?: boolean;
  allowCreateGuest?: boolean;
}) {
  const { input, meta } = useField(name, { validate });
  const [createGuestInitialValues, setCreateGuestInitialValues] = useState<
    Partial<CreateGuestFormValues>
  >({});
  const { clientRequiredFields } = useClientRequiredFieldsAndPod();
  const guestRequiredFields = getGuestLevelFields(
    clientRequiredFields.map((field) => field.value),
  );
  const [isModifying, setIsModifying] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const value = input.value as ClientTeamMemberSelectOption | '';
  const hasInput = input.value !== '';
  const showAutoComplete = !hasInput || isModifying;

  const handleRemove = () => {
    setIsModifying(false);
    input.onChange('');
    onAdd?.();
  };

  const handleAdd = (val: ClientTeamMemberOption | null) => {
    // to make ts happy, val with allowEdpi = false, will always have a value
    if (val && 'id' in val) {
      input.onChange(val);
      setIsModifying(false);
      onAdd?.();
    }
  };

  const openGuestModal = (query: string) => {
    // Sanitize the input first
    const sanitizedQuery = sanitizeUserInput(query);

    // Parse the query to create initial values
    const initialValues: Partial<CreateGuestFormValues> = {};

    if (sanitizedQuery) {
      if (
        looksLikeDODID(sanitizedQuery) &&
        guestRequiredFields.includes(ReservationRequiredField.DodId)
      ) {
        // If it looks like a DoD ID and DoD ID is required, use it as edipi
        initialValues.edipi = sanitizedQuery;
      } else if (!looksLikeDODID(sanitizedQuery)) {
        // If it doesn't look like a DoD ID, parse it as a name
        const { firstName, lastName } = parseNameFromQuery(sanitizedQuery);
        initialValues.firstName = firstName;
        initialValues.lastName = lastName;
      }
    }

    setCreateGuestInitialValues(initialValues);
    setIsCreateModalOpen(true);
  };

  const selectCreatedGuest = (guest: CreatedGuestValues) => {
    input.onChange(guest);
    setIsModifying(false);
    setIsCreateModalOpen(false);
    onAdd?.();
  };

  const handleCloseModal = () => {
    setCreateGuestInitialValues({});
    setIsCreateModalOpen(false);
  };

  const error = meta.error || meta.submitError;

  return (
    <div data-testid={testId}>
      {showAutoComplete ? (
        <ClientTeamMemberAutoComplete
          labelText="Add guests"
          placeholder="Search by ID or Name"
          onAdd={handleAdd}
          onCreateGuest={allowCreateGuest ? openGuestModal : undefined}
          value={
            input.value && 'id' in input.value && input.value.id
              ? (input.value as ClientTeamMemberOption)
              : null
          }
          disabled={disabled}
        />
      ) : (
        value !== '' && (
          <SelectedValueDisplay
            displayText={selectionText(value)}
            onChangeClick={() => setIsModifying(true)}
            onResetClick={handleRemove}
            testId={testId ? `${testId}-selected` : undefined}
          />
        )
      )}
      {meta.touched && error && (
        <span className="text-xs text-red-800">{error}</span>
      )}
      {allowCreateGuest && (
        <CreateGuestModal
          isOpen={isCreateModalOpen}
          onClose={handleCloseModal}
          onGuestCreated={selectCreatedGuest}
          initialValues={createGuestInitialValues}
        />
      )}
    </div>
  );
}
